import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

interface SectionCardProps {
  /** Título do cabeçalho da seção. */
  titulo?: string;
  /** Linha de apoio abaixo do título. */
  subtitulo?: string;
  /** Ícone na caixa à esquerda do título. */
  icone?: ReactNode;
  /** Ações no canto direito do cabeçalho. */
  acoes?: ReactNode;
  /** Remove o padding interno do corpo (para listas/tabelas coladas na borda). */
  semPadding?: boolean;
  className?: string;
  /** Classe extra aplicada ao corpo. */
  corpoClassName?: string;
  children: ReactNode;
}

/**
 * Cartão de seção padrão: borda arredondada, cabeçalho opcional com caixa de ícone +
 * título + subtítulo + ações, e corpo. Substitui os blocos
 * `<Card><CardContent><h2>...</h2>...</CardContent></Card>` ad-hoc das páginas.
 */
export function SectionCard({
  titulo, subtitulo, icone, acoes, semPadding, className, corpoClassName, children,
}: SectionCardProps) {
  const temCabecalho = Boolean(titulo || icone || acoes);
  return (
    <section className={cn('rounded-2xl border border-slate-200 bg-white shadow-sm', className)}>
      {temCabecalho && (
        <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4 sm:px-6">
          {icone !== undefined && (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-institutional/10 text-institutional">
              {icone}
            </div>
          )}
          <div className="min-w-0 flex-1">
            {titulo && <h2 className="text-base font-semibold text-slate-900">{titulo}</h2>}
            {subtitulo && <p className="mt-0.5 text-sm text-slate-500">{subtitulo}</p>}
          </div>
          {acoes && <div className="flex shrink-0 items-center gap-2">{acoes}</div>}
        </div>
      )}
      <div className={cn(!semPadding && 'p-5 sm:p-6', corpoClassName)}>{children}</div>
    </section>
  );
}
